import React, { useCallback, useState, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAudioEditor } from "../../../../context/AudioEditorContext";
import { uploadFileToS3Complete } from "../../../../api/s3";
import { nanoid } from "nanoid";
import { SoundLibraryAI } from "../../../../types/types";
import useAudioEditorNotifications from "../../AudioEditor/hooks/useAudioEditorNotifications";
import styles from "./AudioEditorLoader.module.css";

interface AudioEditorLoaderDropZoneProps {
    children: ReactNode;
}

const AudioEditorLoaderDropZone: React.FC<AudioEditorLoaderDropZoneProps> = ({ children }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const { addToImportedList, setSelectedAudio } = useAudioEditor();
    const { notifyError } = useAudioEditorNotifications();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const dragCounter = React.useRef(0);

    const uploadAudio = useCallback(
        async (file: File) => {
            try {
                setIsUploading(true);
                setErrorMessage(null);

                const token = localStorage.getItem("token");
                if (!token) {
                    throw new Error("No authentication token found");
                }

                const url = await uploadFileToS3Complete(
                    {
                        file,
                        isPublic: true, // Public files for general uploads
                        isFolder: false, // No folder structure for general uploads
                    },
                    token,
                );
                const newAudio: SoundLibraryAI = {
                    id: nanoid(),
                    url,
                    name: file.name.replace(/\.mp3$/i, ""),
                    soundDescription: "",
                };

                // Add to imported list and get the converted audio. Flagged as an upload:
                // the nanoid above is a local key, not a sound library row.
                const convertedAudio = addToImportedList(newAudio, "upload");

                // Set as selected audio if returned
                if (convertedAudio) {
                    setSelectedAudio(convertedAudio);
                }

                setIsUploading(false);
                return Promise.resolve();
            } catch (error) {
                notifyError(error);
                setIsUploading(false);
                return Promise.reject(error);
            }
        },
        [addToImportedList, setSelectedAudio, notifyError],
    );

    const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        if (dragCounter.current === 1) {
            setIsDragging(true);
        }
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter.current = 0;
            setIsDragging(false);
            setErrorMessage(null);

            if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;

            const files = Array.from(e.dataTransfer.files);

            // Check if multiple files are dropped
            if (files.length > 1) {
                setErrorMessage(t("multipleFilesNotSupported"));
                notifyError("Multiple files detected", t("multipleFilesNotSupported"));
            }

            // Filter audio files and take only the first one
            const audioFiles = files.filter((file) => file.type === "audio/mpeg" || file.name.endsWith(".mp3"));

            if (audioFiles.length === 0) {
                setErrorMessage(t("onlyMP3AudioFilesSupported"));
                notifyError("Invalid file type", t("onlyMP3AudioFilesSupported"));
                return;
            }

            // Process only the first file
            const file = audioFiles[0];
            uploadAudio(file).catch(() => {
                setErrorMessage(t("errorUploadingAudioFile"));
                notifyError("Upload failed", t("errorUploadingAudioFile"));
            });
        },
        [uploadAudio, setErrorMessage, t, notifyError],
    );

    return (
        <div
            className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ""} ${isUploading ? styles.uploading : ""}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {children}
            {isUploading && (
                <div className={styles.uploadOverlay}>
                    <div className={styles.spinner}></div>
                </div>
            )}
            {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
        </div>
    );
};

export default AudioEditorLoaderDropZone;
